<template>
  <div class="variable-picker">
    <Button
      ref="triggerRef"
      type="button"
      variant="secondary"
      size="sm"
      icon="mdi:code-braces"
      @click="toggle"
    >
      Insert variable
    </Button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="menuRef"
        class="variable-picker__menu"
        role="menu"
        aria-label="Insert variable"
      >
        <div v-if="isLoading" class="variable-picker__empty">Loading variables…</div>
        <template v-else-if="hasAny">
          <div v-for="group in visibleGroups" :key="group.key" class="variable-picker__group">
            <div class="variable-picker__group-label">{{ group.label }}</div>
            <button
              v-for="field in group.fields"
              :key="field.key"
              type="button"
              class="variable-picker__item"
              @click="select(group.token(field.key))"
            >
              {{ field.label }}
            </button>
          </div>
        </template>
        <div v-else class="variable-picker__empty">No variables available</div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, ref } from "vue";
import { computePosition, autoUpdate, offset, flip, shift, size } from "@floating-ui/dom";
import Button from "@/components/ui/Button.vue";
import { useTemplateVariables } from "@/composables/templates";

const emit = defineEmits<{ insert: [token: string] }>();

const { data, isLoading } = useTemplateVariables();

const groups = computed(() => [
  {
    key: "contact",
    label: "Contact",
    fields: data.value?.contact ?? [],
    token: (key: string) => `{{${key}}}`,
  },
  {
    key: "company",
    label: "Company",
    fields: data.value?.company ?? [],
    token: (key: string) => `{{company.${key}}}`,
  },
  {
    key: "sender",
    label: "You (sender)",
    fields: data.value?.sender ?? [],
    token: (key: string) => `{{sender.${key}}}`,
  },
]);

const visibleGroups = computed(() => groups.value.filter((group) => group.fields.length > 0));
const hasAny = computed(() => visibleGroups.value.length > 0);

const isOpen = ref(false);
const triggerRef = ref<InstanceType<typeof Button> | null>(null);
const menuRef = ref<HTMLElement | null>(null);
let cleanupFloating: (() => void) | null = null;

const positionMenu = () => {
  const referenceEl = triggerRef.value?.$el as HTMLElement | undefined;
  if (!referenceEl || !menuRef.value) return;

  const update = () => {
    if (!referenceEl || !menuRef.value) return;
    computePosition(referenceEl, menuRef.value, {
      placement: "bottom-end",
      strategy: "fixed",
      middleware: [
        offset({ mainAxis: 6, crossAxis: 28 }),
        flip({ padding: 8 }),
        shift({ padding: 8 }),
        size({
          padding: 8,
          apply({ availableHeight, elements }) {
            Object.assign(elements.floating.style, {
              maxHeight: `${Math.max(160, availableHeight)}px`,
            });
          },
        }),
      ],
    }).then(({ x, y }) => {
      if (!menuRef.value) return;
      Object.assign(menuRef.value.style, {
        position: "fixed",
        left: `${x}px`,
        top: `${y}px`,
      });
    });
  };

  cleanupFloating = autoUpdate(referenceEl, menuRef.value, update, {
    ancestorScroll: true,
    ancestorResize: true,
    elementResize: true,
  });
};

const teardownFloating = () => {
  if (cleanupFloating) {
    cleanupFloating();
    cleanupFloating = null;
  }
};

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Node;
  const triggerEl = triggerRef.value?.$el as HTMLElement | undefined;
  if (menuRef.value?.contains(target) || triggerEl?.contains(target)) return;
  close();
};

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === "Escape") close();
};

const open = () => {
  isOpen.value = true;
  requestAnimationFrame(() => {
    positionMenu();
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeydown);
  });
};

const close = () => {
  if (!isOpen.value) return;
  isOpen.value = false;
  teardownFloating();
  document.removeEventListener("mousedown", handleClickOutside);
  document.removeEventListener("keydown", handleKeydown);
};

const toggle = () => (isOpen.value ? close() : open());

const select = (token: string) => {
  emit("insert", token);
  close();
};

onBeforeUnmount(() => {
  teardownFloating();
  document.removeEventListener("mousedown", handleClickOutside);
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
.variable-picker {
  display: inline-block;
}
</style>

<style>
.variable-picker__menu {
  z-index: 10200;
  min-width: 220px;
  max-width: 280px;
  overflow-y: auto;
  padding: 8px;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow:
    0px 4px 6px -1px rgba(0, 0, 0, 0.1),
    0px 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.variable-picker__group + .variable-picker__group {
  margin-top: 6px;
}

.variable-picker__group-label {
  padding: 6px 10px 2px;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #9294a6;
}

.variable-picker__item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 7px 10px;
  font-size: 0.8rem;
  font-family: inherit;
  color: #484964;
  background: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.variable-picker__item:hover {
  background-color: #f9f9fc;
}

.variable-picker__empty {
  padding: 10px;
  font-size: 0.8rem;
  color: #9294a6;
}
</style>
